import { formatDateTimeIn, toLocale } from '@zuruflex/i18n';
import type { AuditActorType, AuditEntryView, AuditVerification } from '@zuruflex/types';
import {
  Alert,
  Badge,
  Button,
  DataTable,
  ErrorState,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  type ColumnDef,
} from '@zuruflex/ui';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { relativeTime } from '../lib/flags.js';
import { useTableLabels } from '../lib/table-labels.js';

const ANY = 'any';

/*
 * What the API writes today. A fixed list rather than one fetched, because
 * a filter that offers only what has happened so far would hide the very
 * category somebody is checking has stayed empty.
 */
const ENTITY_TYPES = [
  'Booking',
  'Payment',
  'Partner',
  'PartnerUser',
  'PartnerDocument',
  'PartnerTerms',
  'Vehicle',
  'CustomerDocument',
  'Customer',
  'AdminUser',
  'Promotion',
  'Review',
  'MessageThread',
  'Setting',
  'feature_flag',
  'provider_route',
  'provider',
] as const;

const ACTOR_TYPES: AuditActorType[] = ['admin', 'partner_user', 'customer', 'service', 'system'];

/** Where an entity lives in the admin, when it has a page of its own. */
function entityLink(entry: AuditEntryView): string | null {
  const id = entry.entityId;
  if (!id) return null;
  const reference = (entry.diff as { reference?: unknown } | null)?.reference;
  switch (entry.entityType) {
    case 'Partner':
      return `/partners/${id}`;
    case 'Vehicle':
      return `/fleet/${id}`;
    case 'Booking':
      return typeof reference === 'string' ? `/bookings/${reference}` : null;
    case 'Promotion':
      return `/promotions/${id}`;
    case 'feature_flag':
      return `/flags/${encodeURIComponent(id)}`;
    default:
      return null;
  }
}

/**
 * Who changed what, for everything the API records — newest first and
 * paged by the chain's own sequence, so a page boundary never splits or
 * repeats a row however fast new ones arrive.
 *
 * The verify button is the point of the hashing: it recomputes the chain
 * in the database and names the first row that was edited, removed, or
 * written with the seal switched off.
 */
export function AuditPage() {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const tableLabels = useTableLabels();
  const { user } = useAuth();
  const canVerify = user?.role === 'OWNER';

  const [entityType, setEntityType] = useState(ANY);
  const [actorType, setActorType] = useState(ANY);
  const [action, setAction] = useState('');
  const [open, setOpen] = useState<AuditEntryView | null>(null);

  const filters = {
    ...(entityType !== ANY ? { entityType } : {}),
    ...(actorType !== ANY ? { actorType: actorType as AuditActorType } : {}),
    ...(action.trim() ? { action: action.trim() } : {}),
  };

  const audit = useInfiniteQuery({
    queryKey: ['audit', filters],
    queryFn: ({ pageParam }) => api.listAudit({ ...filters, take: 50, before: pageParam }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
  const rows = useMemo(() => audit.data?.pages.flatMap((page) => page.items) ?? [], [audit.data]);

  const verify = useMutation({ mutationFn: () => api.verifyAudit() });

  const columns = useMemo<ColumnDef<AuditEntryView>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: t('audit.when'),
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm" title={formatDateTimeIn(row.original.createdAt, locale)}>
            {relativeTime(row.original.createdAt, locale)}
          </span>
        ),
      },
      {
        id: 'actor',
        header: t('audit.who'),
        cell: ({ row }) => <ActorCell entry={row.original} />,
      },
      {
        accessorKey: 'action',
        header: t('audit.action'),
        cell: ({ row }) => (
          <Badge variant="outline" className="font-mono">
            {row.original.action}
          </Badge>
        ),
      },
      {
        id: 'entity',
        header: t('audit.entity'),
        cell: ({ row }) => {
          const href = entityLink(row.original);
          const label = (
            <>
              <span className="font-medium">{row.original.entityType}</span>{' '}
              <span className="font-mono text-xs text-text-muted">
                {shortId(row.original.entityId)}
              </span>
            </>
          );
          return href ? (
            <Link
              to={href}
              className="underline-offset-2 hover:underline"
              onClick={(event) => event.stopPropagation()}
            >
              {label}
            </Link>
          ) : (
            <span>{label}</span>
          );
        },
      },
      {
        accessorKey: 'ip',
        header: t('audit.ip'),
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.ip ?? '—'}</span>,
      },
      {
        accessorKey: 'seq',
        header: '#',
        cell: ({ row }) => <span className="font-mono text-xs text-text-muted">{row.original.seq}</span>,
      },
    ],
    [t, locale],
  );

  return (
    <>
      <PageHeader
        title={t('audit.title')}
        description={t('audit.subtitle')}
        action={
          canVerify ? (
            <Button variant="outline" onClick={() => verify.mutate()} disabled={verify.isPending}>
              <ShieldCheck className="size-4" />
              {verify.isPending ? t('audit.verifying') : t('audit.verify')}
            </Button>
          ) : undefined
        }
      />

      {verify.data ? <VerifyResult result={verify.data} locale={locale} /> : null}
      {verify.error ? <ErrorState error={verify.error} onRetry={() => verify.mutate()} /> : null}

      {audit.error ? (
        <ErrorState error={audit.error} onRetry={() => void audit.refetch()} />
      ) : (
        <>
          <DataTable
            labels={tableLabels}
            columns={columns}
            data={rows}
            isLoading={audit.isPending}
            paginate={false}
            onRowClick={setOpen}
            emptyMessage={t('audit.none')}
            toolbar={
              <div className="flex flex-wrap items-center gap-2">
                <Select value={entityType} onValueChange={setEntityType}>
                  <SelectTrigger className="h-9 w-48" aria-label={t('audit.entity')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY}>{t('audit.allEntities')}</SelectItem>
                    {ENTITY_TYPES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={actorType} onValueChange={setActorType}>
                  <SelectTrigger className="h-9 w-44" aria-label={t('audit.who')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY}>{t('audit.allActors')}</SelectItem>
                    {ACTOR_TYPES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {t(`audit.actorType.${value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  className="h-9 w-56 font-mono"
                  value={action}
                  onChange={(event) => setAction(event.target.value)}
                  placeholder={t('audit.actionPlaceholder')}
                  aria-label={t('audit.action')}
                />
              </div>
            }
          />
          {audit.hasNextPage ? (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                onClick={() => void audit.fetchNextPage()}
                disabled={audit.isFetchingNextPage}
              >
                {audit.isFetchingNextPage ? t('common:state.loading') : t('audit.older')}
              </Button>
            </div>
          ) : null}
        </>
      )}

      <Sheet open={open !== null} onOpenChange={(next) => (next ? null : setOpen(null))}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
          {open ? <EntryDetail entry={open} locale={locale} /> : null}
        </SheetContent>
      </Sheet>
    </>
  );
}

function ActorCell({ entry }: { entry: AuditEntryView }) {
  const { t } = useTranslation('admin');
  const kind = t(`audit.actorType.${entry.actor.type}`);
  if (!entry.actor.id) return <span className="text-sm text-text-muted">{kind}</span>;
  return (
    <div className="min-w-0">
      <p className="truncate text-sm font-medium">{entry.actor.name ?? t('audit.deletedAccount')}</p>
      <p className="truncate text-xs text-text-muted">
        {kind}
        {entry.actor.email ? ` · ${entry.actor.email}` : ''}
      </p>
    </div>
  );
}

function VerifyResult({ result, locale }: { result: AuditVerification; locale: ReturnType<typeof toLocale> }) {
  const { t } = useTranslation('admin');
  const when = formatDateTimeIn(result.verifiedAt, locale);
  return (
    <Alert variant={result.ok ? 'success' : 'danger'} className="mb-4">
      {result.ok
        ? t('audit.verifiedOk', { count: result.checked, when })
        : t('audit.verifiedBroken', {
            seq: result.brokenAt ?? '?',
            reason: result.reason ?? '',
            checked: result.checked,
            when,
          })}
    </Alert>
  );
}

function EntryDetail({ entry, locale }: { entry: AuditEntryView; locale: ReturnType<typeof toLocale> }) {
  const { t } = useTranslation('admin');
  const diff = (entry.diff ?? {}) as Record<string, unknown>;
  const { before, after, ...rest } = diff;
  const facts: [string, string | null][] = [
    [t('audit.when'), formatDateTimeIn(entry.createdAt, locale)],
    [t('audit.who'), [entry.actor.name, entry.actor.email, t(`audit.actorType.${entry.actor.type}`)].filter(Boolean).join(' · ')],
    [t('audit.actorId'), entry.actor.id],
    [t('audit.entity'), `${entry.entityType} ${entry.entityId ?? ''}`.trim()],
    [t('audit.partner'), entry.partnerId],
    [t('audit.ip'), entry.ip],
    [t('audit.userAgent'), entry.userAgent],
    [t('audit.requestId'), entry.requestId],
    ['#', String(entry.seq)],
    [t('audit.hash'), entry.hash],
  ];

  return (
    <>
      <SheetHeader>
        <SheetTitle className="font-mono">{entry.action}</SheetTitle>
        <SheetDescription>{t('audit.detailHint')}</SheetDescription>
      </SheetHeader>
      <dl className="mt-6 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
        {facts
          .filter(([, value]) => value)
          .map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-text-muted">{label}</dt>
              <dd className="break-all font-mono text-xs leading-5">{value}</dd>
            </div>
          ))}
      </dl>
      <div className="mt-6 space-y-4">
        {before !== undefined ? <Json label={t('audit.before')} value={before} /> : null}
        {after !== undefined ? <Json label={t('audit.after')} value={after} /> : null}
        {Object.keys(rest).length > 0 ? <Json label={t('audit.context')} value={rest} /> : null}
      </div>
    </>
  );
}

function Json({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="min-w-0">
      <p className="mb-1 text-xs font-semibold">{label}</p>
      <pre className="max-h-80 overflow-auto rounded-card bg-surface-sunken p-3 font-mono text-xs">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

/** Enough of a uuid to tell rows apart at a glance; the sheet has the rest. */
function shortId(id: string | null): string {
  if (!id) return '';
  return /^[0-9a-f-]{36}$/i.test(id) ? id.slice(0, 8) : id;
}
